import { color } from '@primer-io/goat'
import { AnimatePresence, motion } from 'framer-motion'
import usePrimaryMenuItems from 'hooks/usePrimaryMenuItems'
import useSubMenuItems from 'hooks/useSubMenuItems'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import useFirstRender from '../../hooks/useFirstRender'
import PrimaryMenu from './PrimaryMenu'
import SecondaryMenu from './SecondaryMenu'
import { PRIMARY_MENU_OPEN_WIDTH, PRIMARY_MENU_WIDTH, SIDE_MENU_WIDTH } from './data'

const Root = styled.div`
	position: relative;
	height: 100%;
`

const PrimaryMenuContainer = styled.div`
	position: absolute;
	z-index: 1000;
	top: 0px;
	left: 0px;
	bottom: 0px;

	display: grid;
	height: 100%;

	transition: width 300ms ease;
	pointer-events: all;
`

const SecondaryMenuContainer = styled.div`
	position: absolute;
	z-index: 0;
	top: 0px;
	left: ${p => (p.isFullOpen ? PRIMARY_MENU_OPEN_WIDTH : PRIMARY_MENU_WIDTH)}px;
	bottom: 0px;

	background: ${color('gray-50')};

	transition: all 300ms ease;
`

const SecondaryMenuPanel = ({ isFirstRender, ...props }) => {
	return (
		<motion.div
			style={{}}
			initial={!isFirstRender && { opacity: 0, x: -10, position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
			animate={{ opacity: 1, x: 0, position: 'relative', height: '100%' }}
			exit={{ opacity: 0, x: 10, position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
			transition={{
				type: 'tween',
				duration: 0.3,
				ease: 'easeInOut',
			}}
		>
			<SecondaryMenu {...props} />
		</motion.div>
	)
}

const getPrimaryMenuWidth = ({ isOpen, isPrimaryMenuHover, forcePrimaryMenuClose }) => {
	if (forcePrimaryMenuClose) return isPrimaryMenuHover ? PRIMARY_MENU_OPEN_WIDTH : PRIMARY_MENU_WIDTH
	if (isOpen) return SIDE_MENU_WIDTH
	if (isPrimaryMenuHover) return PRIMARY_MENU_OPEN_WIDTH
	return PRIMARY_MENU_WIDTH
}

export default function SideMenu({ location, forcePrimaryMenuClose }) {
	const firstRender = useFirstRender()
	const { primaryMenuItems, currentPrimaryMenuItem } = usePrimaryMenuItems(location)
	const { platform, sdkItems, secondaryMenuItems, version } = useSubMenuItems(location)
	const [isPrimaryMenuHover, setIsPrimaryMenuHover] = useState(false)

	useEffect(() => {
		setIsPrimaryMenuHover(false)
	}, [location.pathname])

	const isOpen = !secondaryMenuItems?.length

	return (
		<Root>
			<PrimaryMenuContainer
				style={{ width: getPrimaryMenuWidth({ isOpen, isPrimaryMenuHover, forcePrimaryMenuClose }) }}
				onMouseEnter={() => {
					setIsPrimaryMenuHover(true)
				}}
				onMouseLeave={() => {
					setIsPrimaryMenuHover(false)
				}}
			>
				<PrimaryMenu
					menuItems={primaryMenuItems}
					currentMenuItemUrl={currentPrimaryMenuItem?.url}
					isOpen={(!forcePrimaryMenuClose && isOpen) || isPrimaryMenuHover}
					onMouseEnter={() => {
						setIsPrimaryMenuHover(true)
					}}
				/>
			</PrimaryMenuContainer>
			<SecondaryMenuContainer
				style={{
					opacity: !isOpen ? (isPrimaryMenuHover ? 0.5 : 1) : 0,
				}}
			>
				<AnimatePresence>
					{currentPrimaryMenuItem && secondaryMenuItems?.length && (
						<SecondaryMenuPanel
							key={currentPrimaryMenuItem.title}
							isFirstRender={firstRender}
							location={location}
							menuItems={secondaryMenuItems}
							platform={platform}
							sdkItems={sdkItems}
							title={currentPrimaryMenuItem.title}
							version={version}
						/>
					)}
				</AnimatePresence>
			</SecondaryMenuContainer>
		</Root>
	)
}
